import { ref } from 'vue';

// Color modes the app can paint. These map 1:1 onto Bootstrap 5.3's
// `data-bs-theme` attribute values, which drive the whole --bs-* variable layer.
export const SUPPORTED_THEMES = ['light', 'dark'];
export const FALLBACK_THEME = 'light';

// What the user picks in Profile. `auto` is stored but never painted — it is
// resolved to light/dark against the OS setting at apply time.
export const THEME_PREFERENCES = ['light', 'dark', 'auto'];
export const FALLBACK_PREFERENCE = 'light';

const STORAGE_KEY = 'theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

// Browser/PWA chrome above the page. Kept in step with the resolved theme rather
// than the OS: a media-scoped <meta> pair would disagree with the screen for
// anyone who has picked light or dark explicitly.
const THEME_COLORS = { light: '#ffffff', dark: '#14141F' };

/** Narrow any value to a theme the app can paint, or null if unsupported. */
function normalizeTheme(code) {
    if (!code) return null;
    const short = String(code).toLowerCase();
    return SUPPORTED_THEMES.includes(short) ? short : null;
}

/** Narrow any value to a storable preference, or null if unsupported. */
function normalizePreference(code) {
    if (!code) return null;
    const short = String(code).toLowerCase();
    return THEME_PREFERENCES.includes(short) ? short : null;
}

/** What the device is currently set to. Light wherever matchMedia is missing. */
function systemTheme() {
    return window.matchMedia?.(DARK_QUERY).matches ? 'dark' : 'light';
}

/** The theme a preference paints right now. */
function resolveTheme(pref) {
    return pref === 'auto' ? systemTheme() : (normalizeTheme(pref) || FALLBACK_THEME);
}

/**
 * Resolve the preference to boot with:
 *   1. explicitly stored `theme`
 *   2. fallback (light)
 *
 * The OS `prefers-color-scheme` setting is consulted only once the user opts
 * into `auto`. On first run the app looks the same for everyone, whatever their
 * device is set to.
 *
 * Unlike the locale, this is a per-device display preference and is never
 * synced to the user record.
 */
const preference = ref(normalizePreference(localStorage.getItem(STORAGE_KEY)) || FALLBACK_PREFERENCE);

// The painted theme, always 'light' or 'dark' — never 'auto'.
const theme = ref(resolveTheme(preference.value));

/** The stored choice, including 'auto' — bind the Profile control to this. */
export const themePreference = preference;

/** The active theme code, for binding to third-party components. */
export function currentTheme() {
    return theme.value;
}

/** True while dark is active — handy for `:dark` style props. */
export function isDark() {
    return theme.value === 'dark';
}

/** Paint a theme without touching the stored preference. */
function applyTheme(code) {
    theme.value = code;
    document.documentElement.setAttribute('data-bs-theme', code);

    // Seeded in index.html so the first paint is already right; this keeps it
    // correct when the user switches, or when the OS flips under `auto`.
    const meta = document.querySelector('#theme-color-meta');
    if (meta) meta.setAttribute('content', THEME_COLORS[code]);
}

/** Store the user's choice and paint what it resolves to. No-op if unsupported. */
export function setThemePreference(code) {
    const next = normalizePreference(code);
    if (!next) return;
    preference.value = next;
    localStorage.setItem(STORAGE_KEY, next);
    applyTheme(resolveTheme(next));
}

// Follow the device while — and only while — `auto` is the stored choice.
window.matchMedia?.(DARK_QUERY).addEventListener('change', (e) => {
    if (preference.value !== 'auto') return;
    applyTheme(e.matches ? 'dark' : 'light');
});

// Keep <html data-bs-theme> in sync with the boot theme.
applyTheme(theme.value);

export default theme;
